import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Button,
  Badge,
  Input,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Swal from 'sweetalert2';
import ExcelJS from 'exceljs';
import { fetchFeedback, updateFeedback /* , deleteFeedback */ } from '../../redux/QuestionBank/FeedbackAction';
import { fetchQuestionsByIds } from '../../redux/QuestionBank/QuestionsAction';
import { fetchExamQuestionsByIds, fetchFullLengthExamsCached } from '../../redux/QuestionBank/FullLengthExamAction';
import { fetchSubjects, fetchUnits, fetchTopics } from '../../redux/QuestionBank/SubjectsUnitsAction';
import { draftRawToPlainText } from '../../util/draftToHtml';
import { getSpecialty } from 'config/specialtyConfig';
import { getDisplayName } from '../../util/specialtyDisplayHelper';
import Select from 'react-select';
import { customDropdownWithBorder } from '../../util/reactCustomStyles';

const FEEDBACK_TYPE_LABELS = {
  typo: { label: 'Typo', color: 'info', icon: 'fa-spell-check' },
  incorrect_answer: { label: 'Incorrect Answer', color: 'danger', icon: 'fa-times-circle' },
  unclear_question: { label: 'Unclear Question', color: 'warning', icon: 'fa-question-circle' },
  incorrect_explanation: { label: 'Wrong Explanation', color: 'danger', icon: 'fa-book' },
  image_issue: { label: 'Image Issue', color: 'secondary', icon: 'fa-image' },
  other: { label: 'Other', color: 'dark', icon: 'fa-comment' }
};

const STATUS_LABELS = {
  pending: { label: 'Pending', color: 'warning' },
  reviewed: { label: 'Reviewed', color: 'info' },
  resolved: { label: 'Resolved', color: 'success' },
  dismissed: { label: 'Dismissed', color: 'secondary' }
};

const FeedbackList = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { feedback, loading } = useSelector(state => state.questionBankFeedback || { feedback: [], loading: false });
  const { subjects } = useSelector(state => state.questionBankSubjects || { subjects: [] });
  const { units } = useSelector(state => state.questionBankUnits || { units: [] });
  const { topics } = useSelector(state => state.questionBankTopics || { topics: [] });
  const { exams, attemptCounts } = useSelector(state => state.fullLengthExams || { exams: [], attemptCounts: {} });
  const activeSpecialty = useSelector(state => state.specialty.activeSpecialty);

  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('test');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Guards the one-shot exam list fetch used for the attempt warning
  const examsRequestedRef = useRef(false);

  // Load all feedback once on mount
  useEffect(() => {
    dispatch(fetchFeedback());
  }, [dispatch, activeSpecialty]);

  // Filter client-side based on status and source
  const filteredFeedback = feedback.filter(f => {
    if (statusFilter !== 'all' && f.status !== statusFilter) return false;
    const itemSource = f.source || 'test';
    if (sourceFilter !== itemSource) return false;
    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredFeedback.length / itemsPerPage);
  const paginatedFeedback = filteredFeedback.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, sourceFilter]);

  // Load exams + attempt counts once when exam feedback exists (used for the edit warning)
  useEffect(() => {
    if (examsRequestedRef.current) return;
    if (!feedback.some(f => f.source === 'exam')) return;
    if (exams && exams.length > 0) return;
    examsRequestedRef.current = true;
    dispatch(fetchFullLengthExamsCached());
  }, [dispatch, feedback, exams]);

  // Load subjects/units/topics for export (if not already loaded)
  useEffect(() => {
    if (!subjects || subjects.length === 0) dispatch(fetchSubjects());
    if (!units || units.length === 0) dispatch(fetchUnits());
    if (!topics || topics.length === 0) dispatch(fetchTopics());
  }, [dispatch, subjects, units, topics]);

  const handleViewDetails = (item) => {
    setSelectedFeedback(item);
    setAdminNotes(item.adminNotes || '');
    setIsDetailModalOpen(true);
  };

  // Exam feedback lives inside a full-length exam template, not the question bank —
  // deep-link to the exam's edit modal and open the reported question there.
  const handleEditQuestion = async (item) => {
    if (item.source === 'exam' && item.questionId) {
      const match = item.questionId.match(/^(.+)-Q\d{4}$/);
      const examId = match ? match[1] : '';
      if (examId) {
        // Warn before leaving if the exam already has student attempts
        let examList = exams;
        let counts = attemptCounts;
        if (!examList || examList.length === 0) {
          const result = await dispatch(fetchFullLengthExamsCached());
          if (result.success) {
            examList = result.exams || [];
            counts = result.attemptCounts || {};
          }
        }

        const exam = (examList || []).find(e => e.examId === examId);
        const attemptCount = (counts || {})[examId] || 0;

        if (attemptCount > 0) {
          const confirm = await Swal.fire({
            title: 'Edit Exam with Attempts?',
            html: `<p>"<strong>${exam?.name || examId}</strong>" has <strong>${attemptCount} student attempt(s)</strong>.</p><p>Editing questions may affect how past results are displayed. Proceed with caution.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#5e72e4',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, Edit',
            cancelButtonText: 'Cancel'
          });
          if (!confirm.isConfirmed) return;
        }

        history.push(`/admin/question-bank/full-length-exams?examId=${encodeURIComponent(examId)}&questionId=${encodeURIComponent(item.questionId)}`);
        return;
      }
    }
    history.push(`/admin/question-bank/create-question?edit=${item.questionId}`);
  };

  const handleUpdateStatus = async (feedbackId, newStatus) => {
    setUpdating(true);
    const result = await dispatch(updateFeedback(feedbackId, {
      status: newStatus,
      adminNotes: adminNotes,
      resolvedBy: newStatus === 'resolved' ? 'admin' : null
    }));

    if (result.success) {
      Swal.fire({
        icon: 'success',
        title: 'Status Updated',
        text: `Feedback marked as ${newStatus}`,
        timer: 2000,
        showConfirmButton: false
      });
      setIsDetailModalOpen(false);
      dispatch(fetchFeedback());
    } else {
      Swal.fire('Error', result.error || 'Failed to update status', 'error');
    }
    setUpdating(false);
  };

  /* const handleDelete = async (feedbackId) => {
    const confirm = await Swal.fire({
      title: 'Delete Feedback?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f5365c',
      confirmButtonText: 'Yes, delete it'
    });

    if (confirm.isConfirmed) {
      const result = await dispatch(deleteFeedback(feedbackId));
      if (result.success) {
        Swal.fire({
          icon: 'success',
          title: 'Deleted',
          timer: 1500,
          showConfirmButton: false
        });
        setIsDetailModalOpen(false);
      } else {
        Swal.fire('Error', result.error || 'Failed to delete', 'error');
      }
    }
  }; */

  const getPlainText = (content) => {
    if (!content) return '';
    if (typeof content === 'string') return content;
    try {
      return draftRawToPlainText(content);
    } catch {
      return '';
    }
  };

  const getCorrectAnswerLabel = (question) => {
    const correctIds = question.correctAnswer || [];
    if (correctIds.length === 0) return '';
    const choices = question.choices || [];
    const idx = choices.findIndex(c => c.choiceId === correctIds[0]);
    if (idx !== -1) return String.fromCharCode(65 + idx); // A, B, C, D
    // Fallback for index-based IDs
    const idToLabel = { '0': 'A', '1': 'B', '2': 'C', '3': 'D' };
    return idToLabel[correctIds[0]] || '';
  };

  // Helpers for subject/unit/topic display names (matching Bulk Edit format)
  const getSubjectDisplay = (subjectId) => {
    const s = (subjects || []).find(s => s.subjectId === subjectId);
    return s ? getDisplayName(s, activeSpecialty) : '';
  };

  const getUnitDisplay = (unitId) => {
    const u = (units || []).find(u => u.unitId === unitId);
    return u ? getDisplayName(u, activeSpecialty) : '';
  };

  const getTopicDisplay = (topicId) => {
    if (!topicId) return '';
    const t = (topics || []).find(t => t.topicId === topicId);
    return t ? getDisplayName(t, activeSpecialty) : '';
  };

  const handleExportFeedback = async () => {
    if (filteredFeedback.length === 0) {
      Swal.fire('No Feedback', 'There is no feedback to export with the current filter.', 'info');
      return;
    }

    setIsExporting(true);

    try {
      // Separate question IDs by source (test vs exam)
      const testQuestionIds = [...new Set(
        filteredFeedback.filter(f => f.source !== 'exam').map(f => f.questionId).filter(Boolean)
      )];
      const examQuestionIds = [...new Set(
        filteredFeedback.filter(f => f.source === 'exam').map(f => f.questionId).filter(Boolean)
      )];

      // Fetch test questions from question bank, exam questions from exam templates
      const [testResult, examResult] = await Promise.all([
        testQuestionIds.length > 0 ? dispatch(fetchQuestionsByIds(testQuestionIds)) : { success: true, questions: {} },
        examQuestionIds.length > 0 ? dispatch(fetchExamQuestionsByIds(examQuestionIds)) : { success: true, questions: {} }
      ]);

      const questionsMap = {
        ...(testResult.success ? testResult.questions : {}),
        ...(examResult.success ? examResult.questions : {})
      };

      // Build Excel workbook
      const workbook = new ExcelJS.Workbook();
      workbook.creator = getSpecialty(activeSpecialty).name + ' Prep';
      workbook.created = new Date();

      // Sheet name matches what Bulk Edit parser looks for ('questions' or 'export')
      const sheet = workbook.addWorksheet('Questions Export');

      const isExamExport = sourceFilter === 'exam';

      // Exam exports lead with the Exam Code; test exports match the Bulk Edit
      // import format exactly (Question Code first). The trailing four feedback
      // columns are extra context and are ignored by the Bulk Edit parser.
      const headers = [
        ...(isExamExport ? ['Exam Code'] : []),
        'Question Code',
        'Subject Name',
        'Unit Name',
        'Topic Name (Optional)',
        'Question Text',
        'Choice A',
        'Choice B',
        'Choice C',
        'Choice D',
        'Correct Answer',
        'Explanation',
        'Lesson Reference (Optional)',
        'Tags (comma-separated, optional)',
        'Feedback Type',
        'Feedback Description',
        'Feedback Reporter',
        'Feedback Status'
      ];

      sheet.getRow(1).values = headers;
      sheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      sheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF4472C4' }
      };

      sheet.columns = [
        ...(isExamExport ? [{ width: 22 }] : []),  // Exam Code (exam only)
        { width: 22 },  // Question Code
        { width: 22 },  // Subject
        { width: 28 },  // Unit
        { width: 22 },  // Topic
        { width: 50 },  // Question Text
        { width: 30 },  // Choice A
        { width: 30 },  // Choice B
        { width: 30 },  // Choice C
        { width: 30 },  // Choice D
        { width: 15 },  // Correct Answer
        { width: 50 },  // Explanation
        { width: 40 },  // Lesson Reference
        { width: 25 },  // Tags
        { width: 20 },  // Feedback Type
        { width: 50 },  // Feedback Description
        { width: 20 },  // Feedback Reporter
        { width: 15 }   // Feedback Status
      ];

      filteredFeedback.forEach((item, i) => {
        const question = questionsMap[item.questionId] || {};
        const choices = question.choices || [];
        const typeInfo = FEEDBACK_TYPE_LABELS[item.feedbackType] || FEEDBACK_TYPE_LABELS.other;
        const statusInfo = STATUS_LABELS[item.status] || STATUS_LABELS.pending;

        // Extract exam code from composite questionId (e.g., "EXAM-000001" from "EXAM-000001-Q0001")
        let examCode = '';
        if (isExamExport && item.questionId) {
          const match = item.questionId.match(/^(.+)-Q\d{4}$/);
          if (match) examCode = match[1];
        }

        // Exam rows identify the question by its composite exam question id
        const questionCodeValue = isExamExport
          ? (item.questionId || item.questionCode || '')
          : (item.questionCode || question.questionCode || '');

        const row = sheet.getRow(i + 2);
        row.values = [
          ...(isExamExport ? [examCode] : []),
          questionCodeValue,
          getSubjectDisplay(question.subjectId),
          getUnitDisplay(question.unitId),
          getTopicDisplay(question.topicId),
          getPlainText(question.questionText),
          getPlainText(choices[0]?.text),
          getPlainText(choices[1]?.text),
          getPlainText(choices[2]?.text),
          getPlainText(choices[3]?.text),
          getCorrectAnswerLabel(question),
          getPlainText(question.explanationText),
          getPlainText(question.lessonReference),
          (question.tags || []).join(', '),
          typeInfo.label,
          item.description || '',
          item.userName || item.userHandle || 'Unknown',
          statusInfo.label
        ];

        // Highlight the trailing feedback columns with a light yellow background
        for (let c = headers.length - 3; c <= headers.length; c++) {
          row.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFDE7' } };
        }
      });

      // Generate and download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `feedback_export_${sourceFilter}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      Swal.fire({
        icon: 'success',
        title: 'Exported!',
        html: `<p>${filteredFeedback.length} ${isExamExport ? 'exam' : 'test'} feedback items exported with full question details.</p>${isExamExport ? '' : '<p class="text-muted small mt-2">After making corrections, upload the file in <strong>Bulk Upload &rarr; Bulk Edit</strong> to update questions.</p>'}`
      });
    } catch (error) {
      console.error('Export error:', error);
      Swal.fire('Export Failed', error.message || 'Failed to export feedback.', 'error');
    }

    setIsExporting(false);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const pendingCount = feedback.filter(f => f.status === 'pending').length;

  return (
    <>
      <Card className="shadow">
        <CardHeader className="border-0">
          <Row className="align-items-center">
            <Col>
              <h3 className="mb-0">
                <i className="fas fa-comment-alt mr-2 text-warning"></i>
                Question Feedback
                {pendingCount > 0 && statusFilter !== 'pending' && (
                  <Badge color="danger" pill className="ml-2">{pendingCount} pending</Badge>
                )}
              </h3>
            </Col>
            <Col xs="auto" className="d-flex align-items-center">
              <Button
                color="success"
                size="sm"
                className="mr-3"
                onClick={handleExportFeedback}
                disabled={isExporting || loading || filteredFeedback.length === 0}
              >
                {isExporting ? (
                  <Spinner size="sm" className="mr-1" />
                ) : (
                  <i className="fas fa-file-export mr-1"></i>
                )}
                Export {sourceFilter === 'test' ? 'Test' : 'Exam'}{filteredFeedback.length > 0 ? ` (${filteredFeedback.length})` : ''}
              </Button>
              <FormGroup className="mb-0 d-flex align-items-center mr-3">
                <Label className="mr-2 mb-0 text-muted small">Source:</Label>
                <div style={{ minWidth: '120px' }}>
                  <Select
                    options={[
                      { value: 'test', label: 'Test' },
                      { value: 'exam', label: 'Exam' },
                    ]}
                    value={{ value: sourceFilter, label: sourceFilter === 'test' ? 'Test' : 'Exam' }}
                    onChange={(option) => setSourceFilter(option.value)}
                    styles={customDropdownWithBorder}
                    isSearchable={false}
                  />
                </div>
              </FormGroup>
              <FormGroup className="mb-0 d-flex align-items-center">
                <Label className="mr-2 mb-0 text-muted small">Status:</Label>
                <div style={{ minWidth: '140px' }}>
                  <Select
                    options={[
                      { value: 'all', label: 'All' },
                      { value: 'pending', label: 'Pending' },
                      { value: 'reviewed', label: 'Reviewed' },
                      { value: 'resolved', label: 'Resolved' },
                      { value: 'dismissed', label: 'Dismissed' },
                    ]}
                    value={{ value: statusFilter, label: statusFilter === 'all' ? 'All' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1) }}
                    onChange={(option) => setStatusFilter(option.value)}
                    styles={customDropdownWithBorder}
                    isSearchable={false}
                  />
                </div>
              </FormGroup>
            </Col>
          </Row>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-2 text-muted">Loading feedback...</p>
            </div>
          ) : filteredFeedback.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-check-circle fa-3x text-success mb-3"></i>
              <h4>No feedback reports</h4>
              <p className="text-muted">
                {statusFilter === 'pending'
                  ? 'No pending feedback to review.'
                  : 'No feedback found with the selected filter.'}
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table align-items-center table-hover">
                <thead className="thead-light">
                  <tr>
                    <th scope="col">Question</th>
                    <th scope="col">Type</th>
                    <th scope="col">Reported By</th>
                    <th scope="col">Date</th>
                    <th scope="col">Source</th>
                    <th scope="col">Status</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedFeedback.map((item) => {
                    const typeInfo = FEEDBACK_TYPE_LABELS[item.feedbackType] || FEEDBACK_TYPE_LABELS.other;
                    const statusInfo = STATUS_LABELS[item.status] || STATUS_LABELS.pending;

                    return (
                      <tr key={item.feedbackId}>
                        <td>
                          <span className="font-weight-bold text-primary">
                            {item.source === 'exam'
                              ? (item.questionId || item.questionCode || 'N/A')
                              : (item.questionCode || item.questionId?.substring(0, 8) || 'N/A')}
                          </span>
                          {item.passageId && (
                            <Badge color="light" className="ml-2 text-muted">Passage</Badge>
                          )}
                        </td>
                        <td>
                          <Badge color={typeInfo.color}>
                            <i className={`fas ${typeInfo.icon} mr-1`}></i>
                            {typeInfo.label}
                          </Badge>
                        </td>
                        <td>
                          <small>{item.userName || item.userHandle || 'Unknown'}</small>
                        </td>
                        <td>
                          <small className="text-muted">{formatDate(item.createdAt)}</small>
                        </td>
                        <td>
                          <Badge color={item.source === 'exam' ? 'purple' : 'primary'} style={item.source === 'exam' ? { backgroundColor: '#6f42c1', color: '#fff' } : {}}>
                            {item.source === 'exam' ? 'Exam' : 'Test'}
                          </Badge>
                        </td>
                        <td>
                          <Badge color={statusInfo.color}>{statusInfo.label}</Badge>
                        </td>
                        <td>
                          <Button
                            color="primary"
                            size="sm"
                            className="mr-1"
                            onClick={() => handleViewDetails(item)}
                            title="View Details"
                          >
                            <i className="fas fa-eye"></i>
                          </Button>
                          {item.questionId && (
                            <Button
                              color="default"
                              size="sm"
                              onClick={() => handleEditQuestion(item)}
                              title={item.source === 'exam' ? 'Edit in Full-Length Exam' : 'Edit Question'}
                            >
                              <i className="fas fa-pencil-alt"></i>
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-3">
                  <div className="text-muted small mb-2 mb-sm-0">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredFeedback.length)} of {filteredFeedback.length} feedback items
                  </div>
                  <div className="d-flex align-items-center">
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="mr-2"
                    >
                      <i className="fas fa-chevron-left"></i>
                      <span className="d-none d-sm-inline ml-1">Previous</span>
                    </Button>
                    <span className="mx-2 text-nowrap">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="ml-2"
                    >
                      <span className="d-none d-sm-inline mr-1">Next</span>
                      <i className="fas fa-chevron-right"></i>
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Detail Modal */}
      <Modal isOpen={isDetailModalOpen} toggle={() => setIsDetailModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setIsDetailModalOpen(false)}>
          Feedback Details
        </ModalHeader>
        {selectedFeedback && (
          <>
            <ModalBody>
              <Row>
                <Col md="6">
                  <p className="mb-2">
                    <strong>Question:</strong>{' '}
                    <span className="text-primary">{selectedFeedback.questionCode || selectedFeedback.questionId}</span>
                  </p>
                  <p className="mb-2">
                    <strong>Type:</strong>{' '}
                    <Badge color={FEEDBACK_TYPE_LABELS[selectedFeedback.feedbackType]?.color || 'dark'}>
                      {FEEDBACK_TYPE_LABELS[selectedFeedback.feedbackType]?.label || selectedFeedback.feedbackType}
                    </Badge>
                  </p>
                  <p className="mb-2">
                    <strong>Status:</strong>{' '}
                    <Badge color={STATUS_LABELS[selectedFeedback.status]?.color || 'warning'}>
                      {STATUS_LABELS[selectedFeedback.status]?.label || selectedFeedback.status}
                    </Badge>
                  </p>
                </Col>
                <Col md="6">
                  <p className="mb-2">
                    <strong>Reported By:</strong>{' '}
                    {selectedFeedback.userName || selectedFeedback.userHandle}
                  </p>
                  <p className="mb-2">
                    <strong>Date:</strong>{' '}
                    {formatDate(selectedFeedback.createdAt)}
                  </p>
                  <p className="mb-2">
                    <strong>Source:</strong>{' '}
                    <Badge color={selectedFeedback.source === 'exam' ? 'purple' : 'primary'} style={selectedFeedback.source === 'exam' ? { backgroundColor: '#6f42c1', color: '#fff' } : {}}>
                      {selectedFeedback.source === 'exam' ? 'Exam' : 'Test'}
                    </Badge>
                  </p>
                  {selectedFeedback.testId && (
                    <p className="mb-2">
                      <strong>{selectedFeedback.source === 'exam' ? 'Attempt ID' : 'Test ID'}:</strong>{' '}
                      <small>{selectedFeedback.testId}</small>
                    </p>
                  )}
                </Col>
              </Row>

              <hr />

              <div className="mb-3">
                <strong>Description:</strong>
                <div className="bg-lighter p-3 rounded mt-2">
                  {selectedFeedback.description}
                </div>
              </div>

              <FormGroup>
                <Label><strong>Admin Notes:</strong></Label>
                <Input
                  type="textarea"
                  rows="3"
                  placeholder="Add notes about how this was resolved..."
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter className="d-flex justify-content-between">
              {selectedFeedback.questionId && (
                <Button
                  color="default"
                  size="sm"
                  onClick={() => {
                    setIsDetailModalOpen(false);
                    handleEditQuestion(selectedFeedback);
                  }}
                >
                  <i className="fas fa-pencil-alt mr-1"></i>
                  {selectedFeedback.source === 'exam' ? 'Edit in Exam' : 'Edit Question'}
                </Button>
              )}
              {/* <Button
                color="danger"
                outline
                size="sm"
                onClick={() => handleDelete(selectedFeedback.feedbackId)}
                disabled={updating}
              >
                <i className="fas fa-trash mr-1"></i>
                Delete
              </Button> */}
              <div>
                {selectedFeedback.status !== 'dismissed' && (
                  <Button
                    color="warning"
                    className="mr-2"
                    onClick={() => handleUpdateStatus(selectedFeedback.feedbackId, 'dismissed')}
                    disabled={updating}
                  >
                    Dismiss
                  </Button>
                )}
                {selectedFeedback.status !== 'reviewed' && (
                  <Button
                    color="info"
                    className="mr-2"
                    onClick={() => handleUpdateStatus(selectedFeedback.feedbackId, 'reviewed')}
                    disabled={updating}
                  >
                    Mark Reviewed
                  </Button>
                )}
                {selectedFeedback.status !== 'resolved' && (
                  <Button
                    color="success"
                    onClick={() => handleUpdateStatus(selectedFeedback.feedbackId, 'resolved')}
                    disabled={updating}
                  >
                    {updating ? (
                      <Spinner size="sm" />
                    ) : (
                      <>
                        <i className="fas fa-check mr-1"></i>
                        Resolve
                      </>
                    )}
                  </Button>
                )}
              </div>
            </ModalFooter>
          </>
        )}
      </Modal>
    </>
  );
};

export default FeedbackList;
